/**
 * Buffer / proximity — drop a circle around a clicked point and (when a
 * vector point/line/polygon layer with cached features is visible) count
 * how many of its features fall inside.
 */
import * as turf from '@turf/turf';
import { APP } from '../state.js';
import { toast } from '../ui/toast.js';

let userLayersRef = null;

export function registerUserLayers(userLayers) {
  userLayersRef = userLayers;
}

export function activateBuffer(map) {
  APP.activeTool = 'buffer';
  map.getCanvas().style.cursor = 'crosshair';
  toast('Click anywhere on the map to create buffer');
  map.once('click', e => { if (APP.activeTool === 'buffer') runBuffer(map, e.lngLat); });
}

export function runBuffer(map, lngLat) {
  APP.activeTool = null;
  map.getCanvas().style.cursor = '';

  const radius = +document.getElementById('buf-radius').value;
  const center = turf.point([lngLat.lng, lngLat.lat]);
  const circle = turf.circle(center, radius, { units: 'kilometers', steps: 64 });
  map.getSource('buf').setData({ type: 'FeatureCollection', features: [circle] });

  const summaries = [];
  const visible = userLayersRef?.getVisibleVectorLayersWithFeatures?.() || [];
  for (const lyr of visible) {
    const inside = lyr.features.filter(f => {
      try {
        if (!f.geometry) return false;
        if (f.geometry.type === 'Point') {
          return turf.booleanPointInPolygon(f, circle);
        }
        return turf.booleanIntersects(f, circle);
      } catch { return false; }
    });
    if (inside.length) {
      summaries.push({ name: lyr.name, count: inside.length });
    }
  }

  document.getElementById('buf-results').innerHTML = `
    <div class="iso-results">
      <h5>${radius} km buffer</h5>
      ${summaries.length
        ? summaries.map(s => `<div class="iso-stat"><span class="is-l">${escapeHtml(s.name)}</span><span class="is-v">${s.count} feat</span></div>`).join('')
        : `<div style="font-size:11px;color:var(--text3);padding:4px 0">No visible layers intersect this buffer.</div>`}
    </div>
    <button class="btn-sm btn-outline" style="width:100%;margin-top:8px" data-action="clearBuffer">Clear buffer</button>
  `;

  map.flyTo({ center: [lngLat.lng, lngLat.lat], zoom: Math.max(map.getZoom(), 8), duration: 1000 });
  toast(summaries.length
    ? `Buffer · ${summaries.reduce((s, x) => s + x.count, 0)} feature(s) within ${radius} km`
    : `Buffer · ${radius} km`);
}

export function clearBuffer(map) {
  map.getSource('buf').setData({ type: 'FeatureCollection', features: [] });
  document.getElementById('buf-results').innerHTML = '';
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}
